import { BACKEND_URL } from './config';
import { requireSession } from './supabase';
import { drainSseStream, assertSseResponse, SseSilenceError } from './sse';
import { dispatchChatSseEvent } from './chat-sse-dispatch';
import { api } from './api';

/* CUTOVER (Vague 3) : SSE assistant programme sur le backend apps/api (Vertex) */
/* au lieu de la fn edge teacher-chat. Mêmes events SSE. */
const TEACHER_CHAT_URL = `${BACKEND_URL}/teacher/chat`;
import { listStaffThreads, listStaffMessages, type ThreadSummary, type RawWidget } from './chat-threads';

/** Source web renvoyée par recherche_web (grounding Google Search). */
export interface WebSource {
  uri: string;
  title: string;
  domain: string;
}

export interface DocRef {
  document_id: string;
  title?: string | null;
  page: number;
}

/** Enveloppe générique émise par un outil du catalogue tRPC après une écriture
 *  réussie (CHAT_TOOL_RESOURCE_INVALIDATORS côté serveur) — jamais la donnée,
 *  au consommateur de recharger la ressource visée. */
export interface ResourceChangedEvent {
  resource: string;
  id: string;
}

export interface ToolCall {
  name: string;
  args?: { query?: string; document_id?: string; page_from?: number; page_to?: number } & Record<string, unknown>;
  refs?: DocRef[];
  /** Count of items/programmes/themes returned — shown in the chip label after result arrives. */
  itemCount?: number;
  /** Level badges. The backend may send plain strings (CP, CE1…) OR referential
   *  objects ({ code, cycle, label }); the UI coerces both to a display string. */
  levels?: Array<string | { code?: string; cycle?: string; label?: string }>;
}

/** Display string for a level badge, whatever shape the backend sent (a raw
 *  object child would crash React with error #31). */
export function levelLabel(lv: string | { code?: string; cycle?: string; label?: string }): string {
  if (typeof lv === 'string') return lv;
  return lv?.label ?? lv?.code ?? lv?.cycle ?? '';
}

/** @see RawWidget — same shape, re-exported under the local name for callers. */
export type TeacherWidget = RawWidget;

export interface TeacherMsg {
  role: 'user' | 'assistant';
  content: string;
  failed?: boolean;
  clientSendId?: string;
  tools?: ToolCall[];
  widgets?: TeacherWidget[];
  truncated?: true;
  attachments?: TeacherChatAttachment[];
  webSources?: WebSource[];
}


/** Human-readable, source-style label for a tool call (shown inline in chat). */
export function toolLabel(t: ToolCall): string {
  const a = t.args ?? {};
  const str = (v: unknown): string => (v && typeof v === 'string' ? v : '');
  if (t.name === 'search_documents') return `🔎 Recherche : « ${str(a.query)} »`;
  if (t.name === 'read_document_pages') {
    const from = a.page_from;
    const to = a.page_to;
    const pages = from ? (to && to !== from ? `p. ${from}–${to}` : `p. ${from}`) : '';
    return `📖 Lecture du document${pages ? ` (${pages})` : ''}`;
  }
  if (t.name === 'query_program_items') {
    const parts: string[] = [];
    if (str(a['subject'])) parts.push(str(a['subject']));
    if (str(a['level'])) parts.push(str(a['level']).toUpperCase());
    else if (str(a['cycle'])) parts.push(str(a['cycle']));
    if (str(a['text'])) parts.push(`« ${str(a['text'])} »`);
    else if (str(a['kind'])) parts.push(str(a['kind']) + 's');
    const base = `📋 Programme${parts.length ? ' — ' + parts.join(', ') : ''}`;
    return t.itemCount != null ? `${base} · ${t.itemCount} élément${t.itemCount !== 1 ? 's' : ''}` : base;
  }
  if (t.name === 'find_programme') {
    const parts: string[] = [];
    if (str(a['subject'])) parts.push(str(a['subject']));
    if (str(a['level'])) parts.push(str(a['level']).toUpperCase());
    else if (str(a['cycle'])) parts.push(str(a['cycle']));
    const base = `🔍 Localiser le programme${parts.length ? ' — ' + parts.join(', ') : ''}`;
    return t.itemCount != null ? `${base} · ${t.itemCount} programme${t.itemCount !== 1 ? 's' : ''}` : base;
  }
  if (t.name === 'get_programme_outline') {
    const base = `📑 Plan du programme`;
    return t.itemCount != null ? `${base} · ${t.itemCount} thème${t.itemCount !== 1 ? 's' : ''}` : base;
  }
  if (t.name === 'get_progression') {
    const parts: string[] = [];
    if (str(a['subject'])) parts.push(str(a['subject']));
    if (str(a['cycle'])) parts.push(str(a['cycle']));
    if (str(a['text'])) parts.push(`« ${str(a['text'])} »`);
    const base = `📈 Progression${parts.length ? ' — ' + parts.join(', ') : ''}`;
    return t.itemCount != null ? `${base} · ${t.itemCount} élément${t.itemCount !== 1 ? 's' : ''}` : base;
  }
  if (t.name === 'list_curriculum_referential') return `📖 Référentiel matières & niveaux`;
  if (t.name === 'get_widget_recipe') return `Gabarit widget — ${str(a.kind)}`;
  if (t.name === 'get_chart_recipe') return `Gabarit graphique`;
  if (t.name === 'content_get') return `Lecture du document`;
  if (t.name === 'create_section') return `Création section — ${str(a.type) || 'nouvelle'}`;
  if (t.name === 'update_section') return `Mise à jour section`;
  if (t.name === 'reorder_sections') return `Réordonnement des sections`;
  if (t.name === 'content_sections_delete') return `Suppression d'une section`;
  return `⚙️ ${t.name}`;
}

/* checkIsStaff() removed — role checks are table-driven via role_assignments */
/* (see useRoles / checkIsAdmin). The teacher space route was retired too. */

export type TeacherThread = ThreadSummary;

/** The current user's teacher conversations (own threads, mode='teacher'). */
export async function listTeacherThreads(): Promise<TeacherThread[]> {
  return listStaffThreads('teacher');
}

export async function listTeacherMessages(threadId: string): Promise<TeacherMsg[]> {
  return (await listStaffMessages(threadId)) as TeacherMsg[];
}

/** Mirrors ContentDocumentKind (@naia-app/domain) — the API narrows on these
 *  exact values to pick the editor addendum, so never send a display label. */
export type ContentDocumentKind = 'general' | 'course' | 'exercice_sheet' | 'exam';

export interface DocumentContext {
  title: string;
  level: string;
  subject: string;
  kind: ContentDocumentKind;
}

export interface SectionUpdatedPayload {
  id: string;
  document_id: string;
  position: number;
  type: string;
  title: string | null;
  content: unknown;
}

/* Un fait d'infrastructure — budget épuisé, outil en panne, texte retiré — arrive
   par un événement typé et se rend en français fixe. Jamais par la prose du modèle,
   qui l'a décrit faux à chaque fois qu'on l'a laissé faire. */
/** Accusé de démarrage du bootstrap : à cet instant, aucune section n'existe. */
export interface BootstrapStartedPayload {
  jobId: string;
  documentId: string;
  sectionsPlanned: number;
  typicalDurationSeconds: number;
}

export interface ExhaustedPayload {
  rounds: number;
  /** Appels d'écriture réussis, par nom d'outil. */
  writes: Record<string, number>;
}

const plural = (n: number, one: string, many: string): string => (n > 1 ? many : one);

/** Texte fixe affiché à l'enseignant quand le budget d'outils est réellement épuisé. */
export function exhaustedMessage(writes: Record<string, number>): string {
  const created = writes.create_section ?? 0;
  const otherWrites = Object.entries(writes)
    .filter(([name]) => name !== 'create_section')
    .reduce((total, [, n]) => total + n, 0);
  const suffix = "puis j'ai atteint ma limite d'actions pour ce message.";
  if (created > 0) return `J'ai créé ${created} ${plural(created, 'section', 'sections')}, ${suffix}`;
  if (otherWrites > 0) return `J'ai modifié le document, ${suffix}`;
  return "J'ai atteint ma limite d'actions pour ce message.";
}

/** Message envoyé par le bouton « Continuer » — cadre la reprise sans tout refaire. */
export const RESUME_MESSAGE = "Continue exactement là où tu t'es arrêté, sans rien recommencer.";

/** Réessayer après une coupure survenue APRÈS un outil à effet réel : ne rejoue jamais la demande d'origine. */
export const RETRY_AFTER_WRITE_MESSAGE = "La connexion a été coupée en cours de route : certaines modifications ont peut-être déjà été appliquées au document. Ne refais aucune action déjà effectuée (ne recrée pas de section existante) : consulte d'abord l'état actuel du document, puis termine uniquement ce qui manque de ma demande précédente.";

interface StreamCallbacks {
  onDelta: (t: string) => void;
  onSlow?: () => void;
  onActive?: () => void;
  onTool?: (name: string, args: unknown) => void;
  onToolResult?: (name: string, refs: DocRef[], count?: number, levels?: string[]) => void;
  onWidget?: (w: TeacherWidget) => void;
  onThread?: (threadId: string) => void;
  onDone?: (full: string) => void;
  onTruncated?: () => void;
  onError?: (e: Error) => void;
  onWriteTool?: (name: string) => void;
  onExhausted?: (e: ExhaustedPayload) => void;
  /** Le serveur retire un texte déjà diffusé (revendication de limite infondée). */
  onRetract?: () => void;
  onToolError?: (e: { name: string; message: string }) => void;
  onSectionUpdated?: (section: SectionUpdatedPayload) => void;
  onSectionCreated?: (section: SectionUpdatedPayload) => void;
  onSectionsReordered?: (e: { sections: SectionUpdatedPayload[] }) => void;
  onResourceChanged?: (e: ResourceChangedEvent) => void;
  /* Le bootstrap est devenu un travail de fond : le serveur n'annonce plus des
     sections déjà créées mais un travail DÉMARRÉ, dont l'écran suit l'état. */
  onBootstrapStarted?: (e: BootstrapStartedPayload) => void;
  onWebSources?: (e: { sources: WebSource[] }) => void;
}

/** Stream a teacher-chat turn (SSE). Server persists into chat_threads/messages
 *  and returns the threadId via the `meta` event (onThread).
 *  Pass documentId to activate course editor mode (list/read/update_section tools). */
export interface TeacherChatAttachment {
  url: string;
  mime: string;
  storagePath?: string;
}

/** Upload one attachment for the general teacher chat (Assistant programme —
 *  no documentId, so this targets content-assets directly rather than the
 *  document-scoped image import). Same PUT-to-signed-URL shape as chat.tsx's
 *  uploadAttachment (chat-attachments bucket) ; /teacher/chat's downloadAttachment
 *  reads from content-assets, so this is the bucket that must be used here. */
export async function uploadTeacherAttachment(file: File): Promise<TeacherChatAttachment> {
  const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
  const { path, signedUrl } = await api.content.teacherChatUploadUrl.mutate({ ext });

  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error(`upload failed (${put.status})`);

  const url = await api.content.imageReadUrl.query({ path });
  return { url, mime: file.type || 'application/octet-stream', storagePath: path };
}

export async function streamTeacherChat(
  userMessage: string,
  threadId: string | null,
  cb: StreamCallbacks,
  signal?: AbortSignal,
  documentId?: string | null,
  documentContext?: DocumentContext | null,
  attachments?: TeacherChatAttachment[] | null,
  importVariant?: 'subject' | 'answer_key' | null,
  reviewMode?: boolean,
  activeVariantKey?: string | null,
  clientSendId?: string,
): Promise<void> {
  const session = await requireSession();
  let knownThreadId = threadId;
  let retries = 0;
  let writeToolFired = false;

  for (;;) {
    const res = await fetch(TEACHER_CHAT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({
        userMessage,
        ...(clientSendId ? { clientSendId } : {}),
        threadId: knownThreadId,
        ...(documentId ? { documentId } : {}),
        ...(documentContext ? { documentContext } : {}),
        ...(attachments && attachments.length ? { attachments } : {}),
        ...(importVariant ? { importVariant } : {}),
        ...(reviewMode ? { reviewMode } : {}),
        ...(activeVariantKey ? { activeVariantKey } : {}),
      }),
      signal,
    });
    await assertSseResponse(res, 'teacher-chat');

    let completed = false;

    try {
      await drainSseStream(res.body!, ({ event, data }) => {
        try {
          dispatchChatSseEvent(event, data, {
            onDelta: (d) => {
              if (d.text) cb.onDelta(d.text);
            },
            onWriteTool: (d) => {
              writeToolFired = true;
              cb.onWriteTool?.(d.name ?? '');
            },
            onTool: (d) => cb.onTool?.(d.name ?? '', d.args),
            onToolResult: (d) => cb.onToolResult?.(d.name ?? '', (d.refs ?? []) as DocRef[], d.count, d.levels),
            onWidget: (d) => {
              const w = d as { name?: string; html?: string; title?: string | null };
              cb.onWidget?.({ name: w.name ?? 'render_widget', html: w.html ?? '', title: w.title ?? null });
            },
            onMeta: (d) => {
              if (d.threadId) { knownThreadId = d.threadId; cb.onThread?.(d.threadId); }
            },
            onSectionUpdated: (d) => cb.onSectionUpdated?.(d as SectionUpdatedPayload),
            onWebSources: (d) => cb.onWebSources?.(d as { sources: WebSource[] }),
            onSectionCreated: (d) => cb.onSectionCreated?.(d as SectionUpdatedPayload),
            onSectionsReordered: (d) => cb.onSectionsReordered?.(d as { sections: SectionUpdatedPayload[] }),
            onResourceChanged: (d) => cb.onResourceChanged?.(d as ResourceChangedEvent),
            onBootstrapStarted: (d) => cb.onBootstrapStarted?.(d as BootstrapStartedPayload),
            onTruncated: () => cb.onTruncated?.(),
            onExhausted: (d) => cb.onExhausted?.({ rounds: d.rounds ?? 0, writes: d.writes ?? {} }),
            onRetract: () => cb.onRetract?.(),
            onToolError: (d) => cb.onToolError?.({ name: d.name ?? '', message: d.message ?? '' }),
            onDone: (d) => {
              completed = true;
              cb.onDone?.(d.text ?? '');
            },
            onError: (d) => {
              completed = true;
              cb.onError?.(new Error(d.message ?? 'unknown'));
            },
          });
        } catch (e) {
          /* Une trame illisible était avalée sans trace : personne n'apprenait */
          /* qu'un événement s'était perdu en route. */
          console.warn('[teacher-chat] trame SSE illisible —', event, e);
        }
      }, undefined, { onSlow: cb.onSlow, onActive: cb.onActive });
    } catch (e) {
      if (e instanceof SseSilenceError) throw e;
      console.warn('[teacher-chat] stream interrupted:', e);
    }

    if (completed) break;
    if (signal?.aborted) break;
    /* If a write tool already fired before the cut, auto-retry would re-execute mutations. */
    /* Surface a clear error so the user can decide whether to retry manually. */
    if (writeToolFired) {
      cb.onError?.(new Error('__write_partial__'));
      break;
    }
    if (retries >= 2) {
      cb.onError?.(new Error('La connexion a été interrompue. Veuillez réessayer.'));
      break;
    }
    retries++;
    await new Promise<void>((resolve) => setTimeout(resolve, 1500));
    if (signal?.aborted) break;
  }
}
